import { CalculationParams, CalculationResult, ShippingRateRule } from '../types';
import {
  INTERNATIONAL_SHIPPING_RATES,
  DOMESTIC_SHIPPING_RATES,
  PLATFORM_COMMISSION_RATES,
  EXCHANGE_RATE
} from '../constants/shippingRates';

/**
 * 计算国内运费
 */
export const calculateDomesticShipping = (weight: number): number => {
  const { firstKg, additionalPerKg, firstWeight } = DOMESTIC_SHIPPING_RATES;

  if (weight <= firstWeight) {
    return firstKg;
  }

  const additionalWeight = Math.ceil((weight - firstWeight) / 1000);
  return firstKg + additionalWeight * additionalPerKg;
};

/**
 * 计算国际运费
 * 返回包含运费金额和匹配规则的对象
 */
export interface InternationalShippingResult {
  cost: number;
  rule: {
    minPrice: number;
    maxPrice: number;
    minWeight: number;
    maxWeight: number;
    baseFee: number;
    ratePerGram: number;
    maxDimension?: number;
    maxGirth: number;
  };
}

export const calculateInternationalShipping = (
  priceRub: number,
  weight: number,
  dimensions: { length: number; width: number; height: number }
): InternationalShippingResult => {
  const girth = dimensions.length + dimensions.width + dimensions.height;
  const maxDimension = Math.max(dimensions.length, dimensions.width, dimensions.height);

  console.log('开始匹配国际运费规则:', {
    priceRub,
    weight,
    dimensions,
    maxDimension,
    girth
  });

  // 查找适用的运费规则
  const rateRule = INTERNATIONAL_SHIPPING_RATES.find(rule => {
    const priceInRange = priceRub >= rule.minPrice && priceRub <= rule.maxPrice;
    const weightInRange = weight >= rule.minWeight && weight <= rule.maxWeight;
    const dimensionValid = rule.maxDimension === undefined || maxDimension <= rule.maxDimension;
    const girthValid = girth <= rule.maxGirth;
    
    const matches = priceInRange && weightInRange && dimensionValid && girthValid;
    
    // 记录详细的匹配信息用于调试
    console.log('规则匹配检查:', {
      priceRange: `${rule.minPrice}-${rule.maxPrice} RUB`,
      priceInRange,
      weightRange: `${rule.minWeight}-${rule.maxWeight}g`,
      weightInRange,
      maxDimension: rule.maxDimension,
      actualMaxDimension: maxDimension,
      dimensionValid,
      maxGirth: rule.maxGirth,
      actualGirth: girth,
      girthValid,
      matches
    });
    
    return matches;
  });

  if (!rateRule) {
    const errorMsg = `未找到适用的国际运费计算规则。请检查以下条件：\n` +
      `- 价格: ${priceRub} RUB\n` +
      `- 重量: ${weight}g\n` +
      `- 最大尺寸: ${maxDimension}cm\n` +
      `- 周长: ${girth}cm\n\n` +
      `可用规则：\n${INTERNATIONAL_SHIPPING_RATES.map((r, i) => 
        `规则 ${i + 1}: 价格 ${r.minPrice}-${r.maxPrice}RUB, 重量 ${r.minWeight}-${r.maxWeight}g, ` +
        `最大尺寸: ${r.maxDimension || '无限制'}cm, 最大周长: ${r.maxGirth}cm`
      ).join('\n')}`;
      
    console.error(errorMsg);
    throw new Error(errorMsg);
  }

  // 计算运费：基础费用 + 重量 * 每克费率
  const cost = rateRule.baseFee + weight * rateRule.ratePerGram;
  
  return {
    cost,
    rule: {
      minPrice: rateRule.minPrice,
      maxPrice: rateRule.maxPrice,
      minWeight: rateRule.minWeight,
      maxWeight: rateRule.maxWeight,
      baseFee: rateRule.baseFee,
      ratePerGram: rateRule.ratePerGram,
      maxDimension: rateRule.maxDimension,
      maxGirth: rateRule.maxGirth
    }
  };
};

/**
 * 计算平台佣金
 */
export const calculatePlatformCommission = (
  priceRub: number,
  category: 'pet' | 'tool',
  shippingMode: 'standard' | 'border'
): number => {
  const rates = PLATFORM_COMMISSION_RATES[category];
  const commissionRate = priceRub < 1500
    ? rates[shippingMode].below1500
    : rates[shippingMode].above1500;

  return priceRub * commissionRate;
};

/**
 * 计算总成本和利润
 */
export const calculateCosts = ({
  purchasePrice,
  weight,
  dimensions,
  category,
  shippingMode,
  finalSellingPrice, // 最终售价（必填）
}: CalculationParams): CalculationResult => {
  // 1. 计算国内运费
  const domesticShipping = calculateDomesticShipping(weight);
  
  // 2. 计算不含国际运费的总成本
  const totalCostWithoutInternational = purchasePrice + domesticShipping;
  
  // 3. 使用用户输入的最终售价计算国际运费和其他费用
  const finalPriceRmb = finalSellingPrice;
  const finalPriceRub = finalPriceRmb * EXCHANGE_RATE;
  
  // 4. 计算国际运费
  const internationalShipping = calculateInternationalShipping(finalPriceRub, weight, dimensions);
  
  // 5. 计算总成本
  const internationalShippingCost = typeof internationalShipping === 'number' ? 
    internationalShipping : internationalShipping.cost;
  const totalCost = totalCostWithoutInternational + internationalShippingCost;
  
  // 6. 计算平台佣金
  let platformCommissionRate = finalPriceRub < 1500
    ? PLATFORM_COMMISSION_RATES[category][shippingMode].below1500
    : PLATFORM_COMMISSION_RATES[category][shippingMode].above1500;
  
  const platformCommission = finalPriceRmb * platformCommissionRate;
  
  // 7. 计算利润
  const profit = finalPriceRmb - totalCost - platformCommission;
  const profitRate = (profit / finalPriceRmb) * 100;
  
  console.log('计算详情:');
  console.log('采购价:', purchasePrice);
  console.log('国内运费:', domesticShipping);
  console.log('国际运费:', internationalShipping);
  console.log('最终售价(人民币):', finalPriceRmb);
  console.log('最终售价(卢布):', finalPriceRub);
  console.log('使用的国际运费规则:', 
    finalPriceRub >= 1501 && finalPriceRub <= 7000 && weight <= 2000 ? '1501-7000卢布, 1-2000g, 16元+0.025/克' :
    finalPriceRub >= 0 && finalPriceRub <= 1500 && weight >= 501 ? '0-1500卢布, 501-25000g, 23元+0.025/克' :
    '其他规则'
  );
  console.log('总成本(不含国际运费):', totalCostWithoutInternational);
  console.log('总成本(含国际运费):', totalCost);
  // 确保 platformCommissionRate 和 profitRate 是数字
  const safePlatformCommissionRate = platformCommissionRate || 0;
  const safeProfitRate = profitRate || 0;
  
  console.log('平台佣金:', platformCommission, `(${(safePlatformCommissionRate * 100).toFixed(2)}%)`);
  console.log('利润:', profit, `(${safeProfitRate.toFixed(2)}%)`);
  
  // 更新 platformCommissionRate
  platformCommissionRate = safePlatformCommissionRate;

  // 获取国际运费规则描述
  const girth = dimensions.length + dimensions.width + dimensions.height;
  const maxDimension = Math.max(dimensions.length, dimensions.width, dimensions.height);
  
  // 查找适用的国际运费规则
  const rule = INTERNATIONAL_SHIPPING_RATES.find(rule => 
    finalPriceRub >= rule.minPrice &&
    finalPriceRub <= rule.maxPrice &&
    weight >= rule.minWeight &&
    weight <= rule.maxWeight &&
    (rule.maxDimension === undefined || maxDimension <= rule.maxDimension) &&
    (rule.maxGirth === undefined || girth <= rule.maxGirth)
  );

  // 构建国际运费规则描述
  let ruleDescription = '无匹配的运费规则';
  if (rule) {
    ruleDescription = `${rule.minPrice}→${rule.maxPrice}卢布, ${rule.minWeight}克→${rule.maxWeight}克`;
    if (rule.maxDimension) ruleDescription += `, 单边≤${rule.maxDimension}cm`;
    if (rule.maxGirth) ruleDescription += `, 三边和≤${rule.maxGirth}cm`;
    ruleDescription += `, ${rule.baseFee}元+${rule.ratePerGram}元/克`;
  }

  return {
    // 输入参数
    purchasePrice,
    weight,
    dimensions,
    category,
    shippingMode,
    finalSellingPrice,

    // 计算结果
    domesticShipping,
    internationalShipping: internationalShippingCost,
    platformCommission,
    platformCommissionRate,
    totalCost: totalCost,
    suggestedPrice: finalPriceRmb,
    suggestedPriceRub: finalPriceRub,
    finalPriceRub: finalPriceRub,
    profit,
    profitRate,

    // 国际运费规则
    internationalShippingRule: rule ? {
      description: ruleDescription,
      minPrice: rule.minPrice,
      maxPrice: rule.maxPrice,
      minWeight: rule.minWeight,
      maxWeight: rule.maxWeight,
      baseFee: rule.baseFee,
      ratePerGram: rule.ratePerGram,
      maxDimension: rule.maxDimension,
      maxGirth: rule.maxGirth
    } : undefined,
  };
};
